import { useEffect, useRef, useState } from "react";
import api, { fmtNZD, fmtDate, BACKEND_URL } from "@/lib/api";
import NumberInput from "@/components/NumberInput";
import { useAuth } from "@/context/AuthContext";

function SendToXero({ bill, configured, isOwner, onDone }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [ask, setAsk] = useState(false);
  const tid = bill.id;
  async function send(again, confirmed = false) {
    if (!configured) return;
    if (!bill.attachment_path) { setMsg("No PDF or image attached — this bill can't be sent to Xero."); return; }
    if (again && !confirmed) { setAsk(true); return; }
    if (!again && !window.confirm("Send this bill to Xero?")) return;
    setAsk(false);
    setBusy(true); setMsg("");
    try { const r = await api.post(`/bills/${bill.id}/send-xero`, { again }); onDone(r.data); }
    catch (e) { setMsg(e?.response?.data?.detail || "Send failed"); }
    finally { setBusy(false); }
  }
  if (!configured) return <button className="btn-ghost text-xs opacity-60" disabled data-testid={`btn-xero-send-${tid}`}>Xero address not set</button>;
  const sent = bill.sent_to_xero_at;
  return (
    <span className="inline-flex items-center gap-2 flex-wrap">
      <button className="btn-ghost text-xs" disabled={!!sent || busy} onClick={()=>send(false)} data-testid={`btn-xero-send-${tid}`}>
        {sent ? `Sent to Xero · ${fmtDate(sent)}` : busy ? "Sending…" : "Send to Xero"}
      </button>
      {sent && isOwner && <button className="text-xs hub-link" disabled={busy} onClick={()=>send(true)} data-testid={`btn-xero-again-${tid}`}>Send again</button>}
      {ask && (
        <span className="inline-flex items-center gap-2 border border-amber-500/50 bg-amber-500/10 rounded px-2 py-1 text-xs text-amber-200" data-testid={`xero-again-confirm-${tid}`}>
          This bill is already in Xero. Sending again creates a second draft.
          <button className="btn-ghost text-xs" onClick={()=>setAsk(false)} data-testid={`xero-again-cancel-${tid}`}>Cancel</button>
          <button className="btn-silver text-xs" onClick={()=>send(true, true)} data-testid={`xero-again-yes-${tid}`}>Send anyway</button>
        </span>
      )}
      {msg && <span className="text-xs text-amber-300" data-testid={`xero-send-msg-${tid}`}>{msg}</span>}
    </span>
  );
}

const DUP = "Possible Duplicate";

function DupBadge({ bill, where = "held" }) {
  const o = bill.duplicate_info || {};
  return (
    <span className="ml-1 text-amber-300 text-[11px]" data-testid={`bill-dup-badge-${where}-${bill.id}`}>
      ⚠ Possible duplicate — original: {fmtDate(o.date)} · {fmtNZD(o.total)} · {o.project_name || "no project"}
    </span>
  );
}

function DuplicatesSection({ held, onChange }) {
  async function act(b, kind) {
    if (kind === "skip" && !window.confirm("Skip this bill? It will not be imported.")) return;
    try { await api.post(`/bills/${b.id}/duplicate/${kind}`); onChange(); }
    catch (e) { alert(e?.response?.data?.detail || "Failed"); }
  }
  return (
    <div className="card border border-amber-500/40" data-testid="bills-duplicates-section">
      <h3 className="text-silver mb-2">Possible duplicates ({held.length})</h3>
      {held.length === 0 ? <div className="opacity-50 text-sm" data-testid="bills-duplicates-empty">None waiting.</div> : (
        <table className="table"><thead><tr><th>Supplier</th><th>Number</th><th>Date</th><th>Total</th><th>Matches</th><th></th></tr></thead>
          <tbody>{held.map(b => (
            <tr key={b.id} data-testid={`bill-dup-row-${b.id}`}>
              <td>{b.supplier || "—"}</td><td>{b.number || "—"}</td><td>{fmtDate(b.issue_date)}</td><td>{fmtNZD(b.total)}</td>
              <td><DupBadge bill={b} /></td>
              <td className="flex gap-2 flex-wrap">
                <button className="btn-ghost text-xs" onClick={()=>act(b, "skip")} data-testid={`btn-dup-skip-${b.id}`}>Skip (don't import)</button>
                <button className="btn-silver text-xs" onClick={()=>act(b, "import")} data-testid={`btn-dup-import-${b.id}`}>Import anyway</button>
              </td>
            </tr>
          ))}</tbody></table>
      )}
    </div>
  );
}

export default function BillsPanel() {
  const [list, setList] = useState([]);
  const [intake, setIntake] = useState(null);
  const [projects, setProjects] = useState([]);
  const [review, setReview] = useState(null);
  const [selectedAwaiting, setSelectedAwaiting] = useState(new Set());
  const [xeroCfg, setXeroCfg] = useState(false);
  const [tab, setTab] = useState("awaiting");
  const [q, setQ] = useState("");
  const { user } = useAuth();
  const isAdminUser = user?.role === "Admin";
  const [rrMsg, setRrMsg] = useState("");
  const uploadRef = useRef(null);
  const [upResults, setUpResults] = useState([]);
  const [dragOn, setDragOn] = useState(false);
  const [intakeLog, setIntakeLog] = useState([]);

  async function load() {
    const [a, b, p, xc] = await Promise.all([
      api.get("/bills"), api.get("/bills/inbound-address"), api.get("/projects"),
      api.get("/bills/xero-email/status"),
    ]);
    setList(a.data); setIntake(b.data); setProjects(p.data);
    setXeroCfg(!!xc.data.configured);
  }
  const xeroProps = { configured: xeroCfg, isOwner: !!user?.is_owner };
  function afterSend(updated) {
    setList(l => l.map(x => x.id === updated.id ? updated : x));
    setReview(r => (r && r.id === updated.id ? { ...r, ...updated } : r));
  }
  useEffect(() => { load(); }, []);

  async function uploadInvoice(dropped) {
    const files = [...(dropped || uploadRef.current?.files || [])]; if (!files.length) return;
    const out = files.map(f => ({ name: f.name, state: "Waiting" }));
    const extra = [];
    setUpResults([...out]);
    for (let i = 0; i < files.length; i++) {
      const show = () => setUpResults(out.flatMap((r, k) => [r, ...(extra[k] || [])]));
      out[i] = { ...out[i], state: "Reading…" }; show();
      try {
        const fd = new FormData(); fd.append("file", files[i]);
        const { data } = await api.post("/bills/intake/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        const rows = (data.bills || []).map((b, j, all) => {
          const nm = all.length > 1 ? `${files[i].name} (invoice ${j + 1} of ${all.length})` : files[i].name;
          const what = [b.supplier, b.number, b.total ? fmtNZD(b.total) : ""].filter(Boolean).join(" · ");
          return b.status === DUP ? { name: nm, state: "Duplicate", note: `held under Possible duplicates${what ? ` (${what})` : ""}` }
            : { name: nm, state: "Imported", note: b.ai_extracted ? what : "couldn't read the details, fill them in on review" };
        });
        out.splice(i, 1, rows[0] || { name: files[i].name, state: "Failed", note: "nothing came back" });
        extra[i] = rows.slice(1);
      } catch (e) { out[i] = { ...out[i], state: "Failed", note: e?.response?.data?.detail || "upload failed" }; }
      show();
    }
    if (uploadRef.current) uploadRef.current.value = "";
    load(); loadLog();
  }
  const loadLog = () => api.get("/bills/intake/log").then(r => setIntakeLog(r.data)).catch(() => {});
  useEffect(() => { loadLog(); }, []);
  async function saveReview() {
    await api.patch(`/bills/${review.id}/review`, review);
    setReview(null); load();
  }
  async function returnToReview(b) {
    if (!window.confirm("Return this bill to Awaiting Review? Coding and project allocation are kept.")) return;
    try { await api.post(`/bills/${b.id}/return-review`); load(); }
    catch (e) { alert(e?.response?.data?.detail || "Couldn't return to review"); }
  }
  async function setStatus(b, status) { await api.post(`/bills/${b.id}/status`, { status }); load(); }
  async function deleteBill(b, closeReview) {
    if (!window.confirm("Delete this bill?")) return;
    try {
      await api.delete(`/bills/${b.id}`);
      if (closeReview) setReview(null);
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Delete failed (Admin only).");
    }
  }

  const awaiting = list.filter(b => b.status === "Awaiting Review");
  const held = list.filter(b => b.status === DUP);
  const TABS = [
    ["awaiting", "Awaiting review", b => b.status === "Awaiting Review" || b.status === DUP],
    ["unsent", "Approved, not sent", b => (b.status === "Approved" || b.status === "Paid") && !b.sent_to_xero_at],
    ["sent", "Sent to Xero", b => !!b.sent_to_xero_at],
    ["all", "All", () => true],
  ];
  const match = b => !q.trim() || [b.supplier, b.number, b.project_name, String(b.total ?? "")]
    .some(v => (v || "").toLowerCase().includes(q.trim().toLowerCase()));
  const tabRows = list.filter(TABS.find(t => t[0] === tab)[2]).filter(match);

  return (
    <div className="space-y-3" data-testid="bills-panel">
      <div className={`card space-y-2 ${dragOn ? "ring-2 ring-silver" : ""}`} data-testid="bill-drop-zone"
        onDragOver={e => { e.preventDefault(); setDragOn(true); }} onDragLeave={() => setDragOn(false)}
        onDrop={e => { e.preventDefault(); setDragOn(false); uploadInvoice(e.dataTransfer.files); }}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-silver text-sm">Email-to-Bill</div>
          <div className="text-sm opacity-70" data-testid="intake-status">
            Forward invoices to <b className="text-silver">bills@lwandcohub.com</b>
            {intake?.status === "Live" ? " — Live" : " — setup pending"}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <input ref={uploadRef} type="file" multiple accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,application/pdf,image/*" className="text-white text-sm" data-testid="bill-upload-file"/>
          <button className="btn-silver" onClick={() => uploadInvoice()} data-testid="btn-upload-invoice">Upload invoices</button>
        </div>
      </div>
      <div className="text-xs opacity-60">Pick several files at once, or drag PDFs/photos onto this box.</div>
      {upResults.length > 0 && (
        <ul className="text-sm space-y-0.5" data-testid="bill-upload-results">
          {upResults.map((r, i) => (
            <li key={i} data-testid={`bill-upload-result-${i}`}>
              <span className={r.state === "Failed" ? "text-red-400" : r.state === "Duplicate" ? "text-amber-300" : r.state === "Imported" ? "text-green-400" : "opacity-60"}>{r.state}</span> · {r.name}{r.note ? ` — ${r.note}` : ""}
            </li>))}
        </ul>)}
      {intakeLog.length > 0 && (
        <details className="text-sm" data-testid="bill-intake-log">
          <summary className="cursor-pointer opacity-80">Recent emailed invoices ({intakeLog.length})</summary>
          <ul className="mt-1 space-y-1">{intakeLog.map((l, i) => (
            <li key={l.id} data-testid={`bill-intake-log-${i}`}><div className="text-xs opacity-60">{fmtDate(l.at)} · {l.from} · {l.subject}</div>
              {l.results.map((r, j) => <div key={j} className="ml-3"><span className={r.state === "Failed" ? "text-red-400" : r.state === "Duplicate" ? "text-amber-300" : "text-green-400"}>{r.state}</span> · {r.file} — {r.note}</div>)}
            </li>))}</ul>
        </details>)}
      </div>

      <div className="flex gap-2 flex-wrap items-center" data-testid="bills-tabs">
        {TABS.map(([k, l, f]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`bills-tab-${k}`}
            className={`px-3 py-1.5 rounded text-sm ${tab === k ? "bg-silver text-black" : "btn-ghost"}`}>{l} ({list.filter(f).length})</button>
        ))}
        <input className="input flex-1 min-w-[160px] max-w-xs" placeholder="Search supplier, number, project…" value={q} onChange={e => setQ(e.target.value)} data-testid="bills-search" />
      </div>

      {tab === "awaiting" && !q.trim() && <>
      <DuplicatesSection held={held} onChange={load} />

      <div className="card">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
          <h3 className="text-silver">Awaiting Review ({awaiting.length})</h3>
          <div className="flex gap-2 items-center">
            <span className="text-xs opacity-70">{selectedAwaiting.size} selected</span>
            <button className="btn-silver text-xs" disabled={selectedAwaiting.size===0} data-testid="bills-bulk-approve"
                    onClick={async()=>{
                      if (!window.confirm(`Approve ${selectedAwaiting.size} bill${selectedAwaiting.size===1?"":"s"}?`)) return;
                      for (const id of selectedAwaiting) {
                        await api.post(`/bills/${id}/status`, { status: "Approved" });
                      }
                      setSelectedAwaiting(new Set()); load();
                    }}>Approve ({selectedAwaiting.size})</button>
            {selectedAwaiting.size > 0 && (
              <button className="btn-ghost text-xs" onClick={()=>setSelectedAwaiting(new Set())} data-testid="bills-clear-selection">Clear selection</button>
            )}
          </div>
        </div>
        {awaiting.length === 0 ? <div className="opacity-50 text-sm">Nothing to review.</div> : (
          <table className="table"><thead><tr>
            <th><input type="checkbox" checked={awaiting.length>0 && awaiting.every(b=>selectedAwaiting.has(b.id))}
                      onChange={e=>setSelectedAwaiting(e.target.checked ? new Set(awaiting.map(b=>b.id)) : new Set())}
                      data-testid="bills-select-all"/></th>
            <th>Supplier</th><th>Number</th><th>Total</th><th>Project</th><th></th></tr></thead>
            <tbody>{awaiting.map(b => (
              <tr key={b.id} data-testid={`bill-row-${b.id}`}>
                <td><input type="checkbox" checked={selectedAwaiting.has(b.id)}
                           onChange={()=>{const s=new Set(selectedAwaiting); s.has(b.id)?s.delete(b.id):s.add(b.id); setSelectedAwaiting(s);}}
                           data-testid={`bill-select-${b.id}`}/></td>
                <td>{b.supplier || "—"} {b.ai_extracted && <span className="badge badge-silver">AI</span>}
                    </td>
                <td>{b.number || "—"}</td><td>{fmtNZD(b.total)}</td><td>{b.project_name || "—"}</td>
                <td className="flex gap-2">
                  {b.locked ? <span className="text-xs opacity-60" data-testid={`bill-locked-${b.id}`}>Locked{b.reported_at ? " (reported)" : " (project closed)"}</span> : <>
                  <button className="text-xs hub-link" onClick={()=>setReview(b)} data-testid={`bill-review-${b.id}`}>Review</button>
                  <button className="text-xs text-green-400" onClick={()=>setStatus(b,"Approved")}>Approve</button>
                  <button className="text-xs text-red-400" onClick={()=>deleteBill(b)} data-testid={`btn-bill-del-${b.id}`}>Delete</button>
                  </>}
                </td>
              </tr>
            ))}</tbody></table>
        )}
      </div>
      </>}

      {review && (
        <div className="card space-y-2" data-testid="bill-review-form">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className="text-silver">Review bill</h3>
            {review.attachment_path && (<span className="flex gap-3">
              <a className="text-xs hub-link" href={`${BACKEND_URL}/api/bills/${review.id}/attachment`} target="_blank" rel="noreferrer" data-testid="btn-view-invoice">View invoice</a>
              {review.split_count > 1 && review.source_path && <a className="text-xs hub-link" href={`${BACKEND_URL}/api/bills/${review.id}/attachment?source=true`} target="_blank" rel="noreferrer" data-testid="btn-view-source">View whole PDF</a>}
            </span>)}
          </div>
          {review.split_count > 1 && <div className="text-xs opacity-70" data-testid="bill-split-info">Invoice {review.split_index} of {review.split_count} from {review.source_name}{review.source_pages?.length ? ` (page ${review.source_pages.join(", ")})` : ""}</div>}
          {review.intake_summary?.length > 0 && <div className="text-xs opacity-70" data-testid="bill-intake-summary">From the email: {review.intake_summary.map(r => `${r.state} · ${r.file} — ${r.note}`).join(" | ")}</div>}
          {review.review_note && <div className="text-xs text-amber-300" data-testid="bill-review-note">{review.review_note}</div>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <input className="input" placeholder="Supplier" value={review.supplier||""} onChange={e=>setReview({...review,supplier:e.target.value})}/>
            <input className="input" placeholder="Number" value={review.number||""} onChange={e=>setReview({...review,number:e.target.value})}/>
            <input className="input" placeholder="PO ref" value={review.po_ref||""} onChange={e=>setReview({...review,po_ref:e.target.value})}/>
            <NumberInput className="input" money placeholder="Total ex GST" value={review.total||0} onChange={e=>setReview({...review,total:parseFloat(e.target.value||0)})}/>
            <select className="input" value={review.category||"Materials"} onChange={e=>setReview({...review,category:e.target.value})}>
              <option>Materials</option><option>Subcontractors</option><option>Labour</option><option>Other</option>
            </select>
            <select className="input" value={review.project_id||""} onChange={e=>{const p=projects.find(x=>x.id===e.target.value); setReview({...review,project_id:e.target.value, project_name:p?.name||""});}}>
              <option value="">— Allocate to project —</option>
              {projects.map(p=>(<option key={p.id} value={p.id}>{p.name}</option>))}
            </select>
          </div>
          {(review.line_items || []).length > 0 && (
            <table className="table text-xs my-2" data-testid="review-line-items">
              <thead><tr><th>Line item</th><th>Qty</th><th>Unit price</th><th>Line total</th></tr></thead>
              <tbody>{review.line_items.map((l, i) => (
                <tr key={i}><td>{l.description}</td><td>{l.qty ?? ""}</td><td>{l.unit_price != null ? fmtNZD(l.unit_price) : ""}</td><td>{fmtNZD(l.total)}</td></tr>
              ))}</tbody>
            </table>
          )}
          {isAdminUser && !(review.line_items || []).length && review.attachment_path && (
            <button className="btn-ghost text-xs" onClick={async () => { setRrMsg("Reading line items…"); try { const { data: r } = await api.post(`/bills/${review.id}/reread-lines`);
              if (r.status === "added") setReview(x => ({ ...x, line_items: r.lines }));
              setRrMsg(r.status === "added" ? `Added ${r.count} line items (bill total unchanged).` : r.status === "check_lines" ? `Check lines: items add to $${r.sum} but the bill is $${r.total}. Nothing changed.` : "No line items found. Nothing changed."); }
              catch (e) { setRrMsg(e?.response?.data?.detail || "Couldn't read line items"); } }} data-testid="btn-reread-lines">Re-read line items</button>
          )}
          {rrMsg && <div className="text-xs" data-testid="reread-msg">{rrMsg}</div>}
          <div className="flex gap-2">
            <button className="btn-silver" onClick={saveReview} data-testid="btn-save-review">Save</button>
            <button className="btn-silver" onClick={async()=>{await saveReview(); await setStatus(review,"Approved"); setReview(null); load();}}>Save & Approve</button>
            <button className="btn-ghost" onClick={()=>setReview(null)}>Cancel</button>
            {!review.sent_to_xero_at && <button className="btn-ghost text-red-400" onClick={()=>deleteBill(review, true)} data-testid="btn-review-delete">Delete</button>}
            <SendToXero bill={review} {...xeroProps} onDone={afterSend} />
          </div>
        </div>
      )}

      {(tab !== "awaiting" || q.trim()) && <div className="card overflow-x-auto">
        <h3 className="text-silver mb-2">{TABS.find(t => t[0] === tab)[1]}{q.trim() ? ` · matching "${q.trim()}"` : ""} ({tabRows.length})</h3>
        <table className="table"><thead><tr><th>Supplier</th><th>Project</th><th>Total</th><th>Category</th><th>Status</th><th></th></tr></thead>
          <tbody>{tabRows.map(b => (
            <tr key={b.id} data-testid={`bill-all-row-${b.id}`}><td>{b.supplier||"—"} {b.number ? <span className="opacity-60 text-xs">{b.number}</span> : null}
              {b.status === DUP && <DupBadge bill={b} where="all" />}
              </td><td className="text-xs">{b.project_name || "—"}</td><td>{fmtNZD(b.total)}</td><td>{b.category}</td>
              <td><span className="badge badge-grey">{b.status}</span>{b.locked && <span className="ml-1 text-[11px] opacity-70" data-testid={`bill-all-locked-${b.id}`}>Locked</span>}</td>
              <td className="flex gap-1 flex-wrap text-xs">
                {!b.locked && b.status === "Draft" && <button className="text-green-400" onClick={()=>setStatus(b,"Approved")}>Approve</button>}
                {(b.status === "Approved" || b.status === "Paid") && <SendToXero bill={b} {...xeroProps} onDone={afterSend} />}
                {!b.locked && (b.status === "Approved" || b.status === "Paid") && !b.sent_to_xero_at && (
                  <button className="hub-link" onClick={()=>returnToReview(b)} data-testid={`btn-bill-return-${b.id}`}>Return to review</button>
                )}
                {!b.locked && !b.sent_to_xero_at && <button className="text-red-400" onClick={()=>deleteBill(b)} data-testid={`btn-bill-all-del-${b.id}`}>Delete</button>}
              </td>
            </tr>
          ))}
          {tabRows.length === 0 && <tr><td colSpan="6" className="opacity-50">No bills here.</td></tr>}</tbody></table>
      </div>}

    </div>
  );
}
